import { AutorenderApiClient } from '../client/api-client';
import { AutorenderAuthError, AutorenderUploadError } from '../errors';
import { DEFAULT_ASSET_DELIVERY_BASE_URL } from '../constants/defaults';
import type {
  CreateUploaderOptions,
  UploadItem,
  UploadedFileResponse,
  ThemeOptions,
  UploadSourceOption,
  AppRunnerResponse,
  UploadSuccessFile,
} from '../types';
import {
  applyCssVariables,
  buildAssetDeliveryUrl,
  calculateBatchProgress,
  extractPathFromUrl,
  extractWorkspaceFromUrl,
  generateId,
  getRelativeFolderPath,
  sanitizeBaseUrl,
  toCssVars,
} from '../utils/helpers';

const DEFAULT_PARALLEL_UPLOADS = 4;
const MAX_FILE_SIZE_BYTES = 100 * 1024 * 1024; // 100MB

interface FileSelection {
  file: File;
  relativePath?: string;
}

const MIME_TYPE_BY_EXTENSION: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  avif: 'image/avif',
  svg: 'image/svg+xml',
  mp4: 'video/mp4',
  mov: 'video/quicktime',
  webm: 'video/webm',
  mp3: 'audio/mpeg',
  wav: 'audio/wav',
  pdf: 'application/pdf',
};

function inferMimeType(file: File): string {
  if (file.type && file.type !== 'application/octet-stream') {
    return file.type;
  }
  const extension = file.name.split('.').pop()?.toLowerCase();
  if (extension && MIME_TYPE_BY_EXTENSION[extension]) {
    return MIME_TYPE_BY_EXTENSION[extension];
  }
  return 'application/octet-stream';
}

export interface ControllerOptions
  extends Omit<CreateUploaderOptions, 'target' | 'theme' | 'sources'> {
  theme?: ThemeOptions;
  sources?: UploadSourceOption[];
}

export interface ControllerState {
  items: UploadItem[];
  isUploading: boolean;
  progress: number;
  duplicates: string[];
}

type ControllerEvents =
  | 'statechange'
  | 'progress'
  | 'fileprogress'
  | 'error'
  | 'complete'
  | 'filesadded';

export class UploaderController extends EventTarget {
  private options: ControllerOptions;
  private readonly client: AutorenderApiClient;
  private items: UploadItem[] = [];
  private abortController: AbortController | null = null;
  private isUploading = false;
  private targetElement: HTMLElement;
  private duplicateConflicts = new Set<string>();

  private isAbortError(error: unknown): boolean {
    if (!error) return false;
    if (error instanceof DOMException && error.name === 'AbortError') return true;
    if (error instanceof Error && error.name === 'AbortError') return true;
    const message =
      error instanceof Error
        ? error.message
        : typeof error === 'string'
        ? error
        : '';
    return typeof message === 'string' && message.toLowerCase().includes('abort');
  }

  constructor(
    client: AutorenderApiClient,
    target: HTMLElement,
    options: ControllerOptions
  ) {
    super();
    this.client = client;
    this.targetElement = target;
    this.options = options;
    this.applyTheme();
  }

  setOptions(options: Partial<ControllerOptions>) {
    this.options = {
      ...this.options,
      ...options,
    };
    this.applyTheme();
    this.dispatch('statechange');
  }

  getOptions(): ControllerOptions {
    return this.options;
  }

  getState(): ControllerState {
    return {
      items: this.items,
      isUploading: this.isUploading,
      progress: calculateBatchProgress(this.items),
      duplicates: Array.from(this.duplicateConflicts),
    };
  }

  async addFiles(files: FileSelection[]) {
    const existingKeys = new Map<string, UploadItem>();
    const basePathKey = this.normalizePath(this.options.folderPath) ?? '';
    const buildKey = (file: File, folderPath?: string, relativePath?: string) =>
      `${basePathKey}::${folderPath ?? ''}::${relativePath ?? ''}::${file.name}::${file.size}`;

    this.items.forEach(item => {
      existingKeys.set(buildKey(item.file, item.folderPath, item.relativePath), item);
    });

    const filesWithMimeType = files.map(({ file, relativePath }) => ({
      file,
      relativePath,
      mimeType: inferMimeType(file),
    }));

    const newItems = filesWithMimeType.map(({ file, relativePath, mimeType }) => {
      const folderPath = getRelativeFolderPath(
        this.options.folderPath,
        relativePath
      ) ?? '';

      // Only create preview URL for images
      const previewUrl = mimeType.startsWith('image/')
        ? URL.createObjectURL(file)
        : undefined;

      // Check file size limit (100MB)
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
      const info = file.size > MAX_FILE_SIZE_BYTES
        ? `File size (${fileSizeMB}MB) exceeds the maximum limit of 100MB`
        : undefined;

      const baseItem: UploadItem = {
        id: generateId(),
        file,
        relativePath,
        folderPath,
        status: 'pending' as const,
        progress: 0,
        previewUrl,
        info,
      };

      const key = buildKey(file, folderPath, relativePath);
      if (existingKeys.has(key)) {
        baseItem.status = 'completed';
        baseItem.progress = 100;
        baseItem.error = undefined;
        existingKeys.set(key, baseItem);
        return baseItem;
      }

      existingKeys.set(key, baseItem);
      return baseItem;
    });

    this.items = [...this.items, ...newItems];
    this.dispatch('filesadded', { items: newItems });

    const duplicates = newItems.filter(item => item.status === 'completed');
    if (duplicates.length) {
      this.markDuplicatesCompleted(duplicates);
    }

    this.dispatch('statechange');
  }

  removeFile(id: string) {
    const item = this.items.find(entry => entry.id === id);
    if (item?.previewUrl) {
      URL.revokeObjectURL(item.previewUrl);
    }
    this.items = this.items.filter(item => item.id !== id);
    if (item) {
      this.duplicateConflicts.delete(item.file.name);
    }
    this.dispatch('statechange');
  }

  reset() {
    this.abortController?.abort();
    this.abortController = null;
    this.isUploading = false;
    this.cleanupPreviews();
    this.items = [];
    this.duplicateConflicts.clear();
    this.dispatch('statechange');
  }

  async uploadAll() {
    if (this.isUploading) return;
    if (this.items.length === 0) return;

    this.duplicateConflicts.clear();
    this.isUploading = true;
    this.abortController = new AbortController();
    this.dispatch('statechange');

    try {
      await this.processUploads();
      const files = this.buildSuccessFiles();
      this.dispatch('complete', {
        items: this.items,
        files,
      });
    } catch (error) {
      if (this.isAbortError(error)) {
        this.dispatch('error', error);
        return;
      }
      this.annotateItemsWithError(error);
      this.dispatch('error', error);
      throw error;
    } finally {
      this.isUploading = false;
      this.abortController = null;
      this.dispatch('statechange');
    }
  }

  cancel() {
    this.abortController?.abort();
    this.abortController = null;
  }

  private isDuplicateError(error: unknown) {
    if (!error) return false;
    if (typeof error === 'object' && error !== null) {
      const status = (error as any).status ?? (error as any).statusCode;
      if (status === 409) {
        return true;
      }
      const code = (error as any).code;
      if (typeof code === 'string' && code.toLowerCase() === 'conflict') {
        return true;
      }
    }
    const message =
      error instanceof Error
        ? error.message
        : typeof error === 'string'
        ? error
        : '';
    const normalized = message.toLowerCase();
    return (
      normalized.includes('duplicate') ||
      normalized.includes('already exists') ||
      normalized.includes('status 409') ||
      normalized.includes('conflict 409') ||
      normalized.includes('409')
    );
  }

  private markDuplicatesCompleted(
    items: UploadItem[],
    duplicateNames?: string[],
    options: { annotateError?: boolean } = {}
  ): UploadItem[] {
    const normalizedNames =
      duplicateNames
        ?.map(name => name.trim().toLowerCase())
        .filter(Boolean) ?? [];
    const targetSet =
      normalizedNames.length > 0 ? new Set(normalizedNames) : null;

    const targetItems =
      targetSet !== null
        ? items.filter(item =>
            targetSet.has(item.file.name.trim().toLowerCase())
          )
        : items;

    const duplicates =
      targetSet !== null && targetItems.length === 0 ? items : targetItems;

    const { annotateError = true } = options;

    duplicates.forEach(item => {
      if (!item) return;

      const name = item.file.name;
      if (!this.duplicateConflicts.has(name)) {
        this.duplicateConflicts.add(name);
      }

      item.status = 'completed';
      item.progress = 100;
      item.error = annotateError ? 'duplicate' : undefined;
      this.dispatch('fileprogress', { item });
    });

    if (duplicates.length > 0) {
      this.dispatch('progress', {
        progress: calculateBatchProgress(this.items),
        items: this.items,
      });

      this.dispatch('statechange');
    }

    return duplicates;
  }

  private isAuthError(error: unknown): boolean {
    if (error instanceof AutorenderAuthError) {
      return true;
    }

    if (error instanceof AutorenderUploadError) {
      return error.statusCode === 401 || error.statusCode === 403;
    }

    if (!error || typeof error !== 'object') {
      const message =
        error instanceof Error
          ? error.message
          : typeof error === 'string'
          ? error
          : '';
      return (
        typeof message === 'string' &&
        message.toLowerCase().includes('unauthorized')
      );
    }

    const status = (error as any).status ?? (error as any).statusCode;
    if (typeof status === 'number' && (status === 401 || status === 403)) {
      return true;
    }

    const message =
      error instanceof Error
        ? error.message
        : typeof (error as any)?.message === 'string'
        ? (error as any).message
        : '';
    const normalized = String(message).toLowerCase();
    return (
      normalized.includes('unauthorized') ||
      normalized.includes('forbidden') ||
      normalized.includes('invalid api key')
    );
  }

  private annotateItemsWithError(error: unknown) {
    const isAuth = this.isAuthError(error);
    const fallbackMessage =
      error instanceof Error
        ? error.message
        : typeof (error as any)?.message === 'string'
        ? (error as any).message
        : 'Upload failed';

    let mutated = false;

    this.items.forEach(item => {
      if (item.status === 'completed') return;
      mutated = true;
      item.status = 'error';
      item.progress = item.progress ?? 0;
      item.error = isAuth ? 'invalid-api-key' : fallbackMessage;
      this.dispatch('fileprogress', { item });
    });

    if (mutated) {
      this.dispatch('progress', {
        progress: calculateBatchProgress(this.items),
        items: this.items,
      });
      this.dispatch('statechange');
    }
  }

  private async processUploads() {
    const allItems = this.items.filter(item => item.status !== 'completed');

    if (allItems.length === 0) {
      return;
    }

    const parallel = Math.max(1, this.options.parallelUploads ?? DEFAULT_PARALLEL_UPLOADS);
    const uploadSettings = this.options.uploadSettings;

    // Process all items in parallel with concurrency limit
    const tasks = allItems.map(item => async () => {
      if (this.abortController?.signal.aborted) {
        throw new Error('Upload cancelled');
      }

      try {
        await this.uploadSingleFile(item, uploadSettings);
      } catch (error) {
        if (this.isAbortError(error)) {
          return; // Don't mark as error for aborted uploads
        }
        if (this.isDuplicateError(error)) {
          this.markDuplicatesCompleted([item], [item.file.name], {
            annotateError: true,
          });
        } else {
          const err = error instanceof Error ? error : new Error(String(error));
          item.status = 'error';
          item.error = err.message;
          this.dispatch('fileprogress', { item });
          this.dispatch('statechange');
        }
      }
    });

    await this.runWithConcurrency(tasks, parallel);
  }

  private async uploadSingleFile(
    item: UploadItem,
    uploadSettings?: CreateUploaderOptions['uploadSettings']
  ) {
    // Check file size limit before uploading
    if (item.file.size > MAX_FILE_SIZE_BYTES) {
      // Don't upload, but keep status as pending and show info message
      const fileSizeMB = (item.file.size / (1024 * 1024)).toFixed(2);
      item.info = `File size (${fileSizeMB}MB) exceeds the maximum limit of 100MB`;
      this.dispatch('fileprogress', { item });
      this.dispatch('statechange');
      return; // Skip upload for files that are too large
    }

    item.status = 'uploading';
    item.progress = 0;
    this.dispatch('statechange');

    const folderPath = item.folderPath
      ? this.combinePaths(this.options.folderPath, item.folderPath)
      : this.normalizePath(this.options.folderPath);

    const appRunnerResponse: AppRunnerResponse = await this.client.uploadFile(
      {
        file: item.file,
        folderPath: folderPath ?? undefined,
        relativePath: item.relativePath,
        settings: uploadSettings
          ? {
              pretransformations: uploadSettings.pretransformations,
              tags: uploadSettings.tags,
              random_suffix: uploadSettings.random_suffix,
              custom_id: uploadSettings.custom_id,
            }
          : undefined,
      },
      progress => {
        item.progress = progress;
        this.dispatch('fileprogress', { item });
        this.dispatch('progress', {
          progress: calculateBatchProgress(this.items),
          items: this.items,
        });
      },
      this.abortController?.signal
    );

    if (!appRunnerResponse.success) {
      const error = new Error(appRunnerResponse.message ?? 'Upload failed');
      (error as any).code = appRunnerResponse.errorCode;
      throw error;
    }

    item.status = 'completed';
    item.progress = 100;
    item.response = {
      file_no: appRunnerResponse.file_no,
      name: appRunnerResponse.name,
      url: appRunnerResponse.url,
      file_size: appRunnerResponse.file_size,
      folder_no: appRunnerResponse.folder_no,
      path: appRunnerResponse.path,
      width: appRunnerResponse.width,
      height: appRunnerResponse.height,
      format: appRunnerResponse.format,
      workspace_no: appRunnerResponse.workspace_no,
    };
    item.uploadedAt = new Date(appRunnerResponse.created_at);

    if (appRunnerResponse.isDuplicate) {
      item.error = 'duplicate';
    }

    this.dispatch('fileprogress', { item });
    this.dispatch('progress', {
      progress: calculateBatchProgress(this.items),
      items: this.items,
    });
    this.dispatch('statechange');
  }



  private applyTheme() {
    if (!this.options.theme) return;
    const vars = toCssVars(this.options.theme);
    applyCssVariables(this.targetElement, vars);
    if (this.options.theme.appearance && this.options.theme.appearance !== 'system') {
      this.targetElement.dataset.theme = this.options.theme.appearance;
    } else {
      delete this.targetElement.dataset.theme;
    }
  }

  private dispatch(event: ControllerEvents, detail?: CustomEventInit['detail']) {
    const customEvent = new CustomEvent(event, { detail });
    this.dispatchEvent(customEvent);

    switch (event) {
      case 'progress':
        this.options.onProgress?.(
          (detail as any)?.progress ?? calculateBatchProgress(this.items),
          this.items
        );
        break;
      case 'fileprogress':
        this.options.onFileProgress?.((detail as any).item);
        break;
      case 'error':
        this.options.onError?.(
          detail instanceof Error ? detail : new Error(String(detail))
        );
        break;
      case 'complete':
        this.options.onSuccess?.((detail as any)?.files ?? []);
        break;
      default:
        break;
    }
  }

  private resolveAssetDeliveryBaseUrl() {
    return (
      sanitizeBaseUrl(this.options.assetDeliveryBaseUrl) ??
      DEFAULT_ASSET_DELIVERY_BASE_URL
    );
  }

  private buildSuccessFiles(): UploadSuccessFile[] {
    const baseUrl = this.resolveAssetDeliveryBaseUrl();

    return this.items
      .filter(item => item.status === 'completed' && item.response)
      .map(item => {
        const response = item.response as UploadedFileResponse;
        const workspace =
          response.workspace_no ?? extractWorkspaceFromUrl(response.url);
        const assetPath = response.path ?? extractPathFromUrl(response.url);
        const resolvedUrl =
          buildAssetDeliveryUrl(baseUrl, workspace, assetPath, response.url) ??
          response.url;

        return {
          id: item.id,
          previewUrl: item.previewUrl,
          file_no: response.file_no,
          name: response.name,
          url: resolvedUrl,
          input_file_size: item.file?.size ?? response.file_size ?? 0,
          output_file_size: response.file_size ?? 0,
          path: response.path ?? assetPath,
          width: response.width,
          height: response.height,
          format: response.format,
          workspace_no: workspace,
          uploadedAt: item.uploadedAt?.toISOString(),
        };
      });
  }

  private async runWithConcurrency(tasks: Array<() => Promise<void>>, limit: number) {
    const executing = new Set<Promise<void>>();

    for (const task of tasks) {
      if (this.abortController?.signal.aborted) {
        throw new Error('Upload cancelled');
      }

      const promise = task().finally(() => {
        executing.delete(promise);
      });
      executing.add(promise);

      if (executing.size >= limit) {
        await Promise.race(executing);
      }
    }

    await Promise.all(executing);
  }

  private cleanupPreviews() {
    this.items.forEach(item => {
      if (item.previewUrl) {
        URL.revokeObjectURL(item.previewUrl);
        item.previewUrl = undefined;
      }
    });
  }

  private normalizePath(path?: string): string | undefined {
    if (!path) {
      return undefined;
    }

    const normalized = path
      .split('/')
      .map(segment => segment.trim())
      .filter(Boolean)
      .join('/');

    return normalized.length > 0 ? normalized : undefined;
  }

  private combinePaths(basePath?: string, relativePath?: string): string | undefined {
    const base = this.normalizePath(basePath);
    const relative = this.normalizePath(relativePath);

    if (base && relative) {
      return `${base}/${relative}`;
    }

    return base || relative || undefined;
  }

}
